// src/utils/helpers.js
import { DEFAULT_PLANS } from '../config/constants';

// 取得今日日期字串 (YYYY-MM-DD)
export const getTodayKey = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// 讀取 Worker 回傳的文字流
export const safeReadWorkerText = async (resp) => {
  const raw = await resp.text();
  let data = null;
  try {
    data = JSON.parse(raw);
  } catch {
    // ignore
  }
  if (data) {
    const parts = data?.candidates?.[0]?.content?.parts;
    if (Array.isArray(parts)) {
      const text = parts.map(p => p?.text || "").join("").trim();
      return { text, data, raw };
    }
    const fallbackText = (data?.text || data?.result || "").trim();
    return { text: fallbackText, data, raw };
  }
  return { text: (raw || "").trim(), data: null, raw };
};

// 簡單切割 Simple/Detailed
export const splitToSimpleDetailed = (raw) => {
  const text = (raw || "").trim();
  if (!text) return { simple: "", detailed: "" };

  if (text.includes("[DETAILED]")) {
    const [a, b] = text.split("[DETAILED]");
    return { simple: (a || "").trim(), detailed: (b || "").trim() };
  }
  if (text.includes("[DETAILS]")) {
    const [a, b] = text.split("[DETAILS]");
    return { simple: (a || "").trim(), detailed: (b || "").trim() };
  }
  if (text.includes("[簡易解答]") && text.includes("[詳細過程]")) {
    const after = text.split("[簡易解答]")[1] || "";
    const [a, b] = after.split("[詳細過程]");
    return { simple: (a || "").trim(), detailed: (b || "").trim() };
  }
  return { simple: "（已生成內容）", detailed: text };
};

// 壓縮圖片
export const compressThumbnail = (base64Str) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64Str;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_W = 300;
        const scale = MAX_W / img.width;
        canvas.width = MAX_W;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.5));
      };
    });
};

// 壓縮多餘換行
export const compressText = (txt) => (txt || "").replace(/\n{3,}/g, "\n\n").trim();

// 防 Firestore 400：移除 undefined
export const sanitizeForFirestore = (obj, maxLen = 20000) => {
  const seen = new WeakSet();
  const walk = (v) => {
    if (v === undefined) return null;
    if (v === null) return null;
    if (typeof v === "string") {
      const s = v.trim();
      return s.length > maxLen ? s.slice(0, maxLen) : s;
    }
    if (typeof v === "number") {
      if (Number.isNaN(v) || !Number.isFinite(v)) return null;
      return v;
    }
    if (typeof v === "boolean") return v;
    if (v instanceof Date) return v;
    if (Array.isArray(v)) return v.map(walk);
    if (typeof v === "object") {
      if (seen.has(v)) return null;
      seen.add(v);
      const out = {};
      for (const [k, val] of Object.entries(v)) out[k] = walk(val);
      return out;
    }
    return null;
  };
  return walk(obj);
};

// 移除題目中的答案標記 (防暴雷)
export const stripAnswerMarkersFromQuestion = (q) => {
    let s = String(q || "").trim();
    if (!s) return s;
    s = s.replace(/(\n\s*[\(（]簡答[\)）]\s*(答案|Ans|答)?\s*[:：].*)$/is, "");
    s = s.replace(/(\n\s*(答案|Ans|答)\s*[:：]\s*.*)$/is, "");
    s = s.replace(/(\n\s*(Simple Answer|簡答|Short Answer)\s*[:：].*)$/is, "");
    return s.trim();
};

// 答案標準化
export const normalizeAnswerText = (a) => {
    let s = String(a || "").trim();
    if (!s) return s;
    s = s.replace(/^(?:\(|（)?(簡答|答案|Ans|答)(?:\)|）)?\s*[:：]\s*/i, "");
    s = s.replace(/^(?:\(|（)\s*簡答\s*(?:\)|）)\s*/i, "");
    return "答案： " + s.trim();
};

// 重點整理格式化
export const normalizeConceptPair = (keypoints, details) => {
    let kpRaw = String(keypoints || "").trim();
    let dtRaw = String(details || "").trim();

    if (dtRaw === "[object Object]" || !dtRaw) {
        dtRaw = kpRaw;
    }

    let kpList = kpRaw
      .split("\n")
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => s.replace(/^(\-|\•|\*|\d+[\.\、])\s*/g, "").trim())
      .filter(s => s.length > 0 && s !== "--");

    const hasBracketTitle = /【(.*?)】/g;
    const hasHashTitle = /###\s+(.*)/g;
    
    const extractedHeaders = [];
    let match;
    while ((match = hasBracketTitle.exec(dtRaw)) !== null) {
        if (match[1] && match[1].length > 2 && !match[1].includes("步驟")) {
            extractedHeaders.push(match[1]);
        }
    }
    if (extractedHeaders.length === 0) {
        while ((match = hasHashTitle.exec(dtRaw)) !== null) {
            extractedHeaders.push(match[1].trim());
        }
    }

    if (extractedHeaders.length > 0) {
        kpList = extractedHeaders;
        kpRaw = extractedHeaders.join("\n");
    }

    const hasAnyHeadings = /^###\s+|【/m.test(dtRaw);
    if (!hasAnyHeadings && kpList.length > 0) {
      const blocks = kpList.map(t => `### ${t}\n（觀念解析）`);
      return {
        keypoints: kpList.join("\n"),
        details: blocks.join("\n\n") + "\n\n---\n" + dtRaw,
      };
    }

    return { 
        keypoints: kpList.join("\n") || "核心重點整理", 
        details: dtRaw || "核心重點整理" 
    };
};

// 格式化「剩餘額度」顯示
export const getRemainingText = (plan, remaining) => {
  if (remaining == null) return "";
  if (typeof remaining === "object") {
    const { solved, quizzes, concepts } = remaining;
    const s = (v) => (v === Infinity ? "∞" : Math.max(0, Number(v || 0)));
    return `方案：${plan || "free"}｜今日：解題剩 ${s(solved)}／舉一反三剩 ${s(quizzes)}／重點整理剩 ${s(concepts)}`;
  }
  if (remaining === Infinity) return `方案：${plan || "free"}｜剩餘：∞`;
  return `方案：${plan || "free"}｜剩餘：${Math.max(0, Number(remaining || 0))}`;
};

// 格式化「選單內剩餘題數」
export const getRemainingTextForMenu = (userData, plansMap) => {
  if (!userData) return "...";

  const planId = (userData.planId || "A").toUpperCase();
  const planConfig = plansMap[planId] || DEFAULT_PLANS[planId] || DEFAULT_PLANS["A"];

  const numLimit = Number(planConfig.dailyLimit);
  if (numLimit === -1) return "✨ 無限暢用";

  const todayKey = getTodayKey();
  const isToday = userData.quotaDate === todayKey;
  const used = isToday ? (Number(userData.todaySolved) || 0) : 0;

  return `尚餘 ${Math.max(0, numLimit - used)} 題`;
};
